import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icone from './Icone';
import {
  assinar, ligarMotor, entrar, recusar, sair, alternarMic, alternarCam, alternarTela, alternarMao,
  reagir, REACOES, dispositivos, dispositivoAtual, trocarMicrofone, trocarCamera, trocarAltoFalante, nivelMic,
} from '../lib/chamadaEquipe';
import './ChamadaEquipe.css';

/**
 * Tela da chamada de vídeo/voz do chat da equipe, em QUALQUER tela do cockpit
 * (mora no layout, junto do aviso do chat). Mesmo desenho da chamada do HUB AMT:
 * janela que se arrasta pelo topo e muda de tamanho pelo canto, minimizar,
 * maximizar e tela cheia; grade ou destaque; fixar alguém; tela compartilhada
 * em bloco próprio, sem espelhar e sem cortar; quem fala ganha borda.
 */
const POS = 'cockpit.chamadaJanela';

function iniciais(nome) {
  const p = String(nome || '?').trim().split(/\s+/).filter(Boolean);
  return ((p[0] || '?')[0] + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase();
}
function relogio(inicio) {
  const t = Date.parse(inicio);
  if (!Number.isFinite(t)) return '00:00';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  const h = Math.floor(s / 3600);
  return `${h ? `${h}:` : ''}${String(Math.floor(s / 60) % 60).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}
const temVideoVivo = (stream) => !!stream && stream.getVideoTracks().some((t) => t.readyState === 'live' && !t.muted);

function Som({ stream, saida }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream || null;
    if (stream) el.play().catch(() => {});
  }, [stream]);
  useEffect(() => {
    const el = ref.current;
    if (el && saida && el.setSinkId) el.setSinkId(saida).catch(() => {});
  }, [saida]);
  return <audio ref={ref} autoPlay />;
}

function Bloco({ b, emDestaque, fixado, onFixar, reacoes }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.srcObject !== b.stream) el.srcObject = b.stream || null;
    if (b.stream) el.play().catch(() => {});
  }, [b.stream]);
  const comVideo = b.video && temVideoVivo(b.stream);
  const pip = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    if (document.pictureInPictureElement === v) document.exitPictureInPicture().catch(() => {});
    else if (v.requestPictureInPicture) v.requestPictureInPicture().catch(() => {});
  };
  const cls = ['cqv-bloco'];
  if (comVideo) cls.push('com-video');
  if (b.espelho) cls.push('espelho');
  if (b.tela) cls.push('mostrando-tela');
  if (b.falando) cls.push('falando');
  if (emDestaque) cls.push('em-destaque');
  if (fixado) cls.push('fixado');
  if (b.conexao && b.conexao !== 'ok' && !b.eu) cls.push('conectando');
  return (
    <div className={cls.join(' ')} onDoubleClick={() => onFixar(b.id)}>
      <video ref={ref} autoPlay playsInline muted />
      <span className="cqv-avatar">{b.tela ? <Icone nome="tela" tamanho={30} espessura={1.5} /> : iniciais(b.nomeCurto)}</span>
      <div className="cqv-nome">
        {!b.tela && b.mic === false && <span title="Microfone desligado" className="cqv-mudo"><Icone nome="microfone-off" tamanho={13} /></span>}
        <span>{b.tela && <Icone nome="tela" tamanho={13} style={{ marginRight: 5 }} />}{b.rotulo}</span>
      </div>
      {b.mao && <div className="cqv-mao" title="Mão levantada"><Icone nome="mao" tamanho={18} /></div>}
      {b.conexao && b.conexao !== 'ok' && !b.eu && (
        <div className="cqv-conexao">{b.conexao === 'falhou' ? 'Sem conexão com esta pessoa' : b.conexao === 'instavel' ? 'Conexão instável…' : 'Conectando…'}</div>
      )}
      <div className="cqv-bloco-acoes">
        <button type="button" className="cqv-mini" onClick={(e) => { e.stopPropagation(); onFixar(b.id); }} title={fixado ? 'Soltar' : 'Fixar em destaque'} aria-label={fixado ? 'Soltar' : 'Fixar em destaque'}><Icone nome="fixar" tamanho={14} /></button>
        {comVideo && <button type="button" className="cqv-mini" onClick={pip} title="Janela flutuante (picture-in-picture)" aria-label="Janela flutuante"><Icone nome="imagem-flutuante" tamanho={14} /></button>}
      </div>
      {reacoes.map((r) => <span key={r.id} className="cqv-reacao-voando" style={{ left: `${r.x}%` }}>{r.emoji}</span>)}
    </div>
  );
}

function Config({ local }) {
  const [lista, setLista] = useState(null);
  const nivelRef = useRef(null);
  useEffect(() => { dispositivos().then(setLista); }, []);
  useEffect(() => {
    let ativo = true;
    const tic = () => {
      if (!ativo) return;
      if (nivelRef.current) nivelRef.current.style.width = `${nivelMic()}%`;
      requestAnimationFrame(tic);
    };
    tic();
    return () => { ativo = false; };
  }, []);
  if (!lista) return <div className="cqv-pop cqv-config"><p>Carregando…</p></div>;
  const grupo = (rotulo, tipo, aoMudar) => {
    const itens = lista.filter((d) => d.kind === tipo);
    if (!itens.length) return null;
    return (
      <label className="cqv-config-item">
        <span>{rotulo}</span>
        <select defaultValue={dispositivoAtual(tipo)} onChange={(e) => aoMudar(e.target.value)}>
          {itens.map((d, i) => <option key={d.deviceId || i} value={d.deviceId}>{d.label || `${rotulo} ${i + 1}`}</option>)}
        </select>
      </label>
    );
  };
  const temSaida = typeof HTMLMediaElement !== 'undefined' && !!HTMLMediaElement.prototype.setSinkId;
  const algum = lista.some((d) => d.kind === 'audioinput' || d.kind === 'videoinput');
  return (
    <div className="cqv-pop cqv-config" role="dialog" aria-label="Microfone, câmera e alto-falante">
      {grupo('Microfone', 'audioinput', trocarMicrofone)}
      {local.mic !== undefined && (
        <div className="cqv-config-item">
          <span>Teste do microfone (fale algo)</span>
          <div className="cqv-nivel"><i ref={nivelRef} /></div>
        </div>
      )}
      {grupo('Câmera', 'videoinput', trocarCamera)}
      {temSaida && grupo('Alto-falante', 'audiooutput', trocarAltoFalante)}
      {!algum && <p>Libere câmera e microfone no navegador para escolher os aparelhos.</p>}
      <p className="cqv-config-dica">Atalhos: M liga/desliga o microfone · V liga/desliga a câmera · clique duplo num vídeo fixa em destaque.</p>
    </div>
  );
}

function posicaoInicial() {
  let p = null;
  try { p = JSON.parse(localStorage.getItem(POS) || 'null'); } catch (_) { /* ok */ }
  const W = window.innerWidth; const H = window.innerHeight;
  if (p && p.w > 300) return limitar(p.x, p.y, p.w, p.h);
  const w = Math.min(900, Math.round(W * 0.62));
  const h = Math.min(640, H - 120);
  return limitar(W - w - 24, 72, w, h);
}
function limitar(x, y, w, h) {
  const W = window.innerWidth; const H = window.innerHeight;
  w = Math.min(Math.max(320, w), W - 16); h = Math.min(Math.max(240, h), H - 16);
  x = Math.min(Math.max(8, x), W - w - 8); y = Math.min(Math.max(8, y), H - h - 8);
  return { x, y, w, h };
}

export default function ChamadaEquipe() {
  const [s, setS] = useState(null);
  const [, setTique] = useState(0);
  const [modo, setModo] = useState('normal'); // normal | minimizada | maxima
  const [layout, setLayout] = useState('grade'); // grade | destaque
  const [fixado, setFixado] = useState(null);
  const [pop, setPop] = useState(null); // 'reacoes' | 'config'
  const [pos, setPos] = useState(null);
  const janelaRef = useRef(null);

  useEffect(() => ligarMotor(), []);
  useEffect(() => assinar(setS), []);
  const emChamada = !!s && (s.fase === 'em_chamada' || s.fase === 'chamando');

  useEffect(() => {
    if (!emChamada) { setModo('normal'); setFixado(null); setPop(null); return undefined; }
    setPos((p) => p || posicaoInicial());
    const t = setInterval(() => setTique((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [emChamada]);

  useEffect(() => {
    if (!s || s.fase !== 'tocando') return undefined;
    const tecla = (e) => { if (e.key === 'Escape') recusar(); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [s]);

  // Atalhos M (microfone) e V (câmera), fora de campos de texto.
  useEffect(() => {
    if (!emChamada) return undefined;
    const tecla = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.target.closest && e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (e.key === 'm' || e.key === 'M') { e.preventDefault(); alternarMic(); }
      if (e.key === 'v' || e.key === 'V') { e.preventDefault(); alternarCam(); }
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [emChamada]);

  // Janela continua dentro da tela quando o navegador muda de tamanho.
  useEffect(() => {
    if (!emChamada) return undefined;
    const r = () => setPos((p) => (p ? limitar(p.x, p.y, p.w, p.h) : p));
    window.addEventListener('resize', r);
    return () => window.removeEventListener('resize', r);
  }, [emChamada]);

  const guardar = (p) => { try { localStorage.setItem(POS, JSON.stringify(p)); } catch (_) { /* ok */ } };
  const arrastar = useCallback((e) => {
    if (modo !== 'normal' || document.fullscreenElement || (e.target.closest && e.target.closest('button'))) return;
    if (window.innerWidth < 700) return;
    const ini = pos; const dx = e.clientX - ini.x; const dy = e.clientY - ini.y;
    const alvo = e.currentTarget;
    alvo.setPointerCapture?.(e.pointerId);
    janelaRef.current?.classList.add('arrastando');
    let ultimo = ini;
    const mover = (ev) => { ultimo = limitar(ev.clientX - dx, ev.clientY - dy, ini.w, ini.h); setPos(ultimo); };
    const soltar = () => {
      alvo.removeEventListener('pointermove', mover);
      janelaRef.current?.classList.remove('arrastando');
      guardar(ultimo);
    };
    alvo.addEventListener('pointermove', mover);
    alvo.addEventListener('pointerup', soltar, { once: true });
  }, [modo, pos]);
  const redimensionar = useCallback((e) => {
    const ini = pos;
    const alvo = e.currentTarget;
    alvo.setPointerCapture?.(e.pointerId);
    janelaRef.current?.classList.add('arrastando');
    let ultimo = ini;
    const mudar = (ev) => { ultimo = limitar(ini.x, ini.y, ev.clientX - ini.x, ev.clientY - ini.y); setPos(ultimo); };
    const soltar = () => {
      alvo.removeEventListener('pointermove', mudar);
      janelaRef.current?.classList.remove('arrastando');
      guardar(ultimo);
    };
    alvo.addEventListener('pointermove', mudar);
    alvo.addEventListener('pointerup', soltar, { once: true });
  }, [pos]);

  if (!s) return null;
  let meuNome = '';
  try { meuNome = JSON.parse(localStorage.getItem('usuario') || 'null')?.nome || ''; } catch (_) { /* ok */ }

  if (s.fase === 'tocando' && s.convite) {
    const c = s.convite;
    return (
      <div className="cqv-convite" role="alertdialog" aria-label="Chamada recebida">
        <span className="cqv-avatar cqv-pulsa">{iniciais(c.de?.nome)}</span>
        <div className="cqv-convite-txt">
          <strong>{c.de?.nome || 'Alguém'}</strong>
          <span>{c.video ? 'Chamada de vídeo' : 'Chamada de voz'}{c.canal_tipo !== 'direto' && c.canal_nome ? ` · ${c.canal_nome}` : ''}</span>
        </div>
        <div className="cqv-convite-acoes">
          <button type="button" className="cqv-ctl cqv-recusar" onClick={recusar} title="Recusar" aria-label="Recusar"><Icone nome="telefone-desligar" tamanho={20} /></button>
          <button type="button" className="cqv-ctl cqv-atender" onClick={() => entrar(c.canal_id, c.canal_nome, { video: false })} title="Atender só com áudio" aria-label="Atender só com áudio"><Icone nome="telefone" tamanho={20} /></button>
          <button type="button" className="cqv-ctl cqv-atender" onClick={() => entrar(c.canal_id, c.canal_nome, { video: true })} title="Atender com vídeo" aria-label="Atender com vídeo"><Icone nome="video" tamanho={21} /></button>
        </div>
      </div>
    );
  }

  if (s.fase === 'encerrada') return <div className="cqv-fim" role="status"><Icone nome="telefone-desligar" /> {s.aviso || 'Chamada encerrada'}</div>;
  if (!emChamada || !pos) return null;

  // Blocos: cada pessoa, a tela de quem compartilha (à parte), eu e a minha tela.
  const pares = [...s.pares.entries()];
  const blocos = [];
  for (const [uid, p] of pares) {
    blocos.push({ id: String(uid), nomeCurto: p.nome, rotulo: p.nome, stream: p.remoto, video: p.cam, mic: p.mic, mao: p.mao, falando: p.falando, conexao: p.conexao });
    if (p.tela && p.telaStream) blocos.push({ id: `${uid}:tela`, nomeCurto: p.nome, rotulo: `Tela de ${p.nome}`, stream: p.telaStream, video: true, tela: true, conexao: p.conexao });
  }
  blocos.push({ id: 'eu', eu: true, nomeCurto: meuNome, rotulo: `${meuNome || 'Você'} (você)`, stream: s.local.camStream, video: s.local.cam, espelho: true, mic: s.local.mic, mao: s.local.mao, falando: s.local.falando });
  if (s.local.tela && s.local.telaStream) blocos.push({ id: 'eu:tela', eu: true, nomeCurto: meuNome, rotulo: 'Sua tela (é o que os outros veem)', stream: s.local.telaStream, video: true, tela: true });

  const telaRemota = blocos.find((b) => b.tela && !b.eu);
  const alvo = (fixado && blocos.find((b) => b.id === fixado))
    || telaRemota
    || (layout === 'destaque' ? (blocos.find((b) => b.falando && !b.eu) || blocos.find((b) => !b.eu)) : null);
  const destaque = !!alvo && blocos.length > 1 && (layout === 'destaque' || !!fixado || !!telaRemota);
  const n = Math.min(blocos.length, 6);
  const pessoas = pares.length + 1;
  const reacoesDe = (b) => (b.tela ? [] : s.reacoes.filter((r) => (b.eu ? r.quem === 'eu' : String(r.quem) === b.id)));
  const fixar = (id) => { setFixado((f) => (f === id ? null : id)); };

  const cheia = () => {
    const el = janelaRef.current;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
  };
  const estiloJanela = modo === 'normal' && window.innerWidth >= 700
    ? { left: pos.x, top: pos.y, width: pos.w, height: pos.h }
    : undefined;

  return (
    <div
      ref={janelaRef}
      className={`cqv-janela ${modo}`}
      style={estiloJanela}
      role="dialog"
      aria-label="Chamada"
      onClick={(e) => { if (modo === 'minimizada' && !(e.target.closest && e.target.closest('button'))) setModo('normal'); }}
    >
      {pares.map(([uid, p]) => <Som key={`som-${uid}`} stream={p.remoto} saida={s.prefs.som} />)}
      <div className="cqv-topo" onPointerDown={arrastar} title={modo === 'normal' ? 'Arraste para mover' : undefined}>
        <span className="cqv-ponto" />
        <b className="cqv-titulo">{s.canalNome || 'Chamada'}</b>
        <span className="cqv-tempo">{s.fase === 'chamando' ? 'Chamando…' : relogio(s.inicio)}</span>
        <span className="cqv-pessoas" title="Pessoas na chamada"><Icone nome="usuarios" tamanho={14} /> {pessoas}</span>
        <span className="cqv-espaco" />
        {modo !== 'minimizada' && (
          <button type="button" className="cqv-btn-topo" onClick={() => setLayout((l) => (l === 'grade' ? 'destaque' : 'grade'))} title={layout === 'grade' ? 'Ver em destaque' : 'Ver em grade'} aria-label="Alternar grade e destaque"><Icone nome={layout === 'grade' ? 'destaque' : 'grade'} tamanho={16} /></button>
        )}
        <button type="button" className="cqv-btn-topo" onClick={() => setModo((m) => (m === 'minimizada' ? 'normal' : 'minimizada'))} title={modo === 'minimizada' ? 'Abrir a chamada' : 'Minimizar (continua na chamada)'} aria-label={modo === 'minimizada' ? 'Abrir a chamada' : 'Minimizar'}><Icone nome={modo === 'minimizada' ? 'maximizar' : 'minimizar'} tamanho={16} /></button>
        {modo !== 'minimizada' && (
          <>
            <button type="button" className="cqv-btn-topo" onClick={() => setModo((m) => (m === 'maxima' ? 'normal' : 'maxima'))} title={modo === 'maxima' ? 'Voltar ao tamanho da janela' : 'Maximizar na página'} aria-label="Maximizar"><Icone nome={modo === 'maxima' ? 'minimizar' : 'maximizar'} tamanho={16} /></button>
            <button type="button" className="cqv-btn-topo" onClick={cheia} title="Tela cheia" aria-label="Tela cheia"><Icone nome="tela-cheia" tamanho={16} /></button>
          </>
        )}
      </div>
      {s.aviso && modo !== 'minimizada' && <div className="cqv-aviso">{s.aviso}</div>}
      <div className="cqv-palco">
        <div className={`cqv-grade${destaque ? ' modo-destaque' : ''}`} data-n={n}>
          {pares.length === 0 && (
            <div className="cqv-bloco cqv-esperando">
              <span className="cqv-avatar cqv-pulsa">{iniciais(s.canalNome)}</span>
              <div className="cqv-nome"><span>{s.fase === 'chamando' ? `Chamando ${s.canalNome}…` : 'Esperando os outros entrarem…'}</span></div>
            </div>
          )}
          {blocos.map((b) => (
            <Bloco key={b.id} b={b} emDestaque={destaque && alvo && alvo.id === b.id} fixado={fixado === b.id} onFixar={fixar} reacoes={reacoesDe(b)} />
          ))}
        </div>
        <div className="cqv-brindes" aria-live="polite">
          {s.brindes.map((x) => <div key={x.id} className="cqv-brinde">{x.texto}</div>)}
        </div>
      </div>
      <div className="cqv-barra">
        <button type="button" className={`cqv-ctl${s.local.mic ? '' : ' desligado'}`} onClick={alternarMic} title={s.local.mic ? 'Desligar microfone (M)' : 'Ligar microfone (M)'} aria-label={s.local.mic ? 'Desligar microfone' : 'Ligar microfone'} aria-pressed={!s.local.mic}><Icone nome={s.local.mic ? 'microfone' : 'microfone-off'} tamanho={21} /></button>
        <button type="button" className={`cqv-ctl${s.local.cam ? '' : ' desligado'}`} onClick={alternarCam} title={s.local.cam ? 'Desligar câmera (V)' : 'Ligar câmera (V)'} aria-label={s.local.cam ? 'Desligar câmera' : 'Ligar câmera'} aria-pressed={!s.local.cam}><Icone nome={s.local.cam ? 'video' : 'video-off'} tamanho={22} /></button>
        {modo !== 'minimizada' && (
          <>
            <button type="button" className={`cqv-ctl${s.local.tela ? ' ativo' : ''}`} onClick={alternarTela} title={s.local.tela ? 'Parar de compartilhar' : 'Compartilhar a tela'} aria-label={s.local.tela ? 'Parar de compartilhar' : 'Compartilhar a tela'} aria-pressed={s.local.tela}><Icone nome="compartilhar-tela" tamanho={21} /></button>
            <button type="button" className={`cqv-ctl${s.local.mao ? ' ativo' : ''}`} onClick={alternarMao} title={s.local.mao ? 'Baixar a mão' : 'Levantar a mão'} aria-label={s.local.mao ? 'Baixar a mão' : 'Levantar a mão'} aria-pressed={s.local.mao}><Icone nome="mao" tamanho={21} /></button>
            <button type="button" className={`cqv-ctl${pop === 'reacoes' ? ' ativo' : ''}`} onClick={() => setPop((p) => (p === 'reacoes' ? null : 'reacoes'))} title="Reações" aria-label="Reações"><Icone nome="sorriso" tamanho={21} /></button>
            <button type="button" className={`cqv-ctl${pop === 'config' ? ' ativo' : ''}`} onClick={() => setPop((p) => (p === 'config' ? null : 'config'))} title="Microfone, câmera e alto-falante" aria-label="Microfone, câmera e alto-falante"><Icone nome="engrenagem" tamanho={21} /></button>
          </>
        )}
        <button type="button" className="cqv-ctl cqv-sair" onClick={sair} title="Sair da chamada" aria-label="Sair da chamada"><Icone nome="telefone-desligar" tamanho={22} /></button>
        {pop === 'reacoes' && modo !== 'minimizada' && (
          <div className="cqv-pop cqv-reacoes">
            {REACOES.map((e) => <button key={e} type="button" className="cqv-reacao" onClick={() => { reagir(e); setPop(null); }} aria-label={`Reagir ${e}`}>{e}</button>)}
          </div>
        )}
        {pop === 'config' && modo !== 'minimizada' && <Config local={s.local} />}
      </div>
      {modo === 'normal' && <div className="cqv-alca" onPointerDown={redimensionar} title="Arraste para mudar o tamanho" />}
    </div>
  );
}
